import { api } from './api';

export interface DashboardStatistics {
  documentStatistics: DocumentStatistics;
  userStatistics: UserStatistics;
  teamStatistics: TeamStatistics;
  searchStatistics: SearchStatistics;
  lastUpdated: string;
}

export interface DocumentStatistics {
  totalDocuments: number;
  documentsCreatedToday: number;
  documentsCreatedThisWeek: number;
  documentsCreatedThisMonth: number;
  documentsInProcessing: number;
  documentsCompleted: number;
  documentsPendingApproval: number;
  documentsRejected: number;
  totalFolders: number;
  documentsByStatus: DocumentStatusCount[];
  documentsByType: DocumentTypeCount[];
  documentTrends: DocumentTrend[];
}

export interface UserStatistics {
  totalUsers: number;
  activeUsers: number;
  inactiveUsers: number;
  usersCreatedToday: number;
  usersCreatedThisWeek: number;
  usersCreatedThisMonth: number;
  mostActiveUsers: MostActiveUser[];
  userActivityTrends: UserActivityTrend[];
}

export interface TeamStatistics {
  totalTeams: number;
  activeTeams: number;
  teamsCreatedToday: number;
  teamsCreatedThisWeek: number;
  teamsCreatedThisMonth: number;
  teamsByMemberCount: TeamMemberCount[];
}

export interface SearchStatistics {
  totalSearches: number;
  searchesToday: number;
  searchesThisWeek: number;
  searchesThisMonth: number;
  mostSearchedDocuments: MostSearchedDocument[];
  searchTrends: SearchTrend[];
  searchesByType: SearchTypeCount[];
}

export interface DocumentStatusCount {
  status: string;
  count: number;
  percentage: number;
}

export interface DocumentTypeCount {
  type: string;
  count: number;
  percentage: number;
}

export interface DocumentTrend {
  date: string;
  documentsCreated: number;
  documentsCompleted: number;
  documentsInProcessing: number;
}

export interface MostActiveUser {
  userId: string;
  userName: string;
  email: string;
  loginCount: number;
  documentsAccessed: number;
  searchesPerformed: number;
  lastActivity: string;
}

export interface UserActivityTrend {
  date: string;
  activeUsers: number;
  newUsers: number;
  totalLogins: number;
}

export interface TeamMemberCount {
  teamName: string;
  memberCount: number;
  isActive: boolean;
}

export interface MostSearchedDocument {
  documentId: string;
  documentTitle: string;
  documentPath: string;
  searchCount: number;
  lastSearched: string;
  documentType: string;
}

export interface SearchTrend {
  date: string;
  totalSearches: number;
  successfulSearches: number;
  failedSearches: number;
}

export interface SearchTypeCount {
  searchType: string;
  count: number;
  percentage: number;
}

class DashboardService {
  private updateInterval: NodeJS.Timeout | null = null;
  private updateCallback: ((stats: DashboardStatistics) => void) | null = null;
  private readonly UPDATE_INTERVAL_MS = 5 * 60 * 1000; // 5 minutos

  async getDashboardStatistics(): Promise<DashboardStatistics> {
    try {
      const response = await api.get('/dashboard/statistics');
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar estatísticas do dashboard:', error);
      throw error;
    }
  }

  async getDocumentStatistics(): Promise<DocumentStatistics> {
    try {
      const response = await api.get('/dashboard/documents');
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar estatísticas de documentos:', error);
      throw error;
    }
  }

  async getUserStatistics(): Promise<UserStatistics> {
    try {
      const response = await api.get('/dashboard/users');
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar estatísticas de usuários:', error);
      throw error;
    }
  }

  async getTeamStatistics(): Promise<TeamStatistics> {
    try {
      const response = await api.get('/dashboard/teams');
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar estatísticas de equipes:', error);
      throw error;
    }
  }

  async getSearchStatistics(): Promise<SearchStatistics> {
    try {
      const response = await api.get('/dashboard/search');
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar estatísticas de busca:', error);
      throw error;
    }
  }

  async getMostActiveUsers(limit: number = 10): Promise<MostActiveUser[]> {
    try {
      const response = await api.get(`/dashboard/users/most-active?limit=${limit}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar usuários mais ativos:', error);
      throw error;
    }
  }

  async getMostSearchedDocuments(limit: number = 10): Promise<any[]> {
    const response = await api.get(`/dashboard/documents/most-searched?limit=${limit}`);
    return response.data;
  }

  async getRecentDocuments(limit: number = 3): Promise<any[]> {
    const response = await api.get(`/dashboard/documents/recent?limit=${limit}`);
    return response.data;
  }

  // Iniciar atualização automática
  startAutoUpdate(callback: (stats: DashboardStatistics) => void): void {
    this.updateCallback = callback;
    
    // Executar imediatamente
    this.getDashboardStatistics().then(stats => {
      if (this.updateCallback) {
        this.updateCallback(stats);
      }
    });
    
    // Configurar intervalo de atualização
    this.updateInterval = setInterval(async () => {
      try {
        if (this.updateCallback) {
          const stats = await this.getDashboardStatistics();
          this.updateCallback(stats);
        }
      } catch (error) {
        console.error('Erro na atualização automática do dashboard:', error);
      }
    }, this.UPDATE_INTERVAL_MS);
  }

  // Parar atualização automática
  stopAutoUpdate(): void {
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
      this.updateInterval = null;
      this.updateCallback = null;
    }
  }

  // Forçar atualização manual
  async forceUpdate(): Promise<DashboardStatistics> {
    try {
      const stats = await this.getDashboardStatistics();
      if (this.updateCallback) {
        this.updateCallback(stats);
      }
      return stats;
    } catch (error) {
      console.error('Erro na atualização forçada do dashboard:', error);
      throw error;
    }
  }
}

export const dashboardService = new DashboardService();

